<template>
    <div>
        <NoQuoteModal
            ref="noQuoteModal"
            :qNoQuoteReason="qNoQuoteReason"
            @update:qNoQuoteReason="qNoQuoteReason = $event"
            @submitReason="handleNoQuoteReason"
        />
        <Transition name="quote-modal-fade" appear @after-leave="emitHide">
            <div v-if="showModal" :class="['quote_modal', { minimized: isMinimized }]">
                <div class="columns">
                    <div class="field column">
                        <div class="quote-modal-header">
                            <h5>{{ headerCustomer }}</h5>
                            <span v-if="searchTypeBadge" class="quote-modal-badge text-uppercase">
                                {{ searchTypeBadge }}
                            </span>
                            <button
                                type="button"
                                class="button is-small minimize-btn quote-modal-icon-button"
                                :aria-label="
                                    isMinimized ? 'Expand quote form' : 'Minimize quote form'
                                "
                                @click="toggleMinimize"
                            >
                                <font-awesome-icon
                                    :icon="isMinimized ? 'fa-solid fa-plus' : 'fa-solid fa-minus'"
                                />
                            </button>
                            <button class="delete" aria-label="close" @click="hideQuote()"></button>
                        </div>
                    </div>
                </div>
                <form v-show="!isMinimized" autocomplete="off">
                    <div class="quote-form-grid">
                        <div class="field">
                            <label class="form-label small mb-1">Part</label>
                            <input
                                id="q_part_number"
                                v-model="q_part_selected"
                                type="text"
                                :class="fieldClass('form-control form-control-sm', 'part_number')"
                                name="part_number"
                                autocomplete="off"
                                disabled
                            />
                        </div>

                        <div class="field">
                            <label class="form-label small mb-1">Part Quoted</label>
                            <input
                                id="q_part_quoted"
                                v-model="q_part_quoted"
                                type="text"
                                :class="fieldClass('form-control form-control-sm', 'part_quoted')"
                                name="part_quoted"
                                autocomplete="off"
                            />
                        </div>

                        <div class="field full-width">
                            <label class="form-label small mb-1">Description</label>
                            <input
                                id="q_description"
                                v-model="q_description"
                                @blur="q_description = descriptionText(q_description)"
                                type="text"
                                :class="fieldClass('form-control form-control-sm', 'description')"
                                name="description"
                                autocomplete="off"
                            />
                        </div>

                        <div class="field">
                            <label class="form-label small mb-1">Vendor</label>
                            <InputSelectDropdown
                                v-model="q_vendor"
                                input-id="q_vendor"
                                select-id="q_vendor_select"
                                :options="vendorOptions"
                                :select-size="6"
                                :input-class="fieldClass('form-control form-control-sm', 'vendor')"
                                :input-attrs="{ autocomplete: 'off' }"
                            />
                        </div>

                        <div class="field" v-if="isRetail">
                            <label class="form-label small mb-1">Condition Code</label>
                            <select
                                id="q_condition"
                                v-model="q_condition"
                                :class="fieldClass('form-select form-select-sm', 'condition')"
                                name="condition"
                            >
                                <option value=""></option>
                                <option
                                    v-for="conditionCode in conditionCodeOptions"
                                    :key="conditionCode"
                                    :value="conditionCode"
                                >
                                    {{ conditionCode }}
                                </option>
                            </select>
                        </div>

                        <div class="field">
                            <label class="form-label small mb-1">Quantity</label>
                            <input
                                id="q_quantity"
                                @keyup="edit_quote"
                                @change="edit_quote"
                                v-model="q_quantity"
                                type="number"
                                :class="fieldClass('form-control form-control-sm', 'quantity')"
                                name="quantity"
                                autocomplete="off"
                            />
                        </div>

                        <div class="field">
                            <label class="form-label small mb-1">Replacement Cost</label>
                            <input
                                id="q_unit_cost"
                                v-model="q_unit_cost"
                                type="number"
                                :class="fieldClass('form-control form-control-sm', 'unit_cost')"
                                name="cost"
                                autocomplete="off"
                            />
                        </div>

                        <div class="field">
                            <label class="form-label small mb-1">List Price</label>
                            <input
                                id="q_list_price"
                                v-model="q_list_price"
                                type="number"
                                :class="fieldClass('form-control form-control-sm', 'list_price')"
                                name="list_price"
                                autocomplete="off"
                            />
                        </div>

                        <div class="field">
                            <div class="field-pair">
                                <div class="field-stack">
                                    <label class="form-label small mb-1">UOM</label>
                                    <InputSelectDropdown
                                        v-model="q_unit_of_measure"
                                        input-id="q_unit_of_measure"
                                        select-id="q_uom_select"
                                        :options="uom_items"
                                        :input-class="
                                            fieldClass(
                                                'form-control form-control-sm',
                                                'unit_of_measure'
                                            )
                                        "
                                        :input-attrs="{ style: 'width: 5em', autocomplete: 'off' }"
                                        :select-size="Math.max(uom_items.length, 5)"
                                    />
                                </div>
                                <div class="field-stack">
                                    <label class="form-label small mb-1">Margin</label>
                                    <InputSelectDropdown
                                        :model-value="q_margin"
                                        input-id="q_margin"
                                        select-id="q_margin_select"
                                        input-type="number"
                                        :options="marginOptions"
                                        :input-class="
                                            fieldClass('form-control form-control-sm', 'margin')
                                        "
                                        :input-attrs="{
                                            min: 0,
                                            step: 0.1,
                                            style: 'width: 5em',
                                            autocomplete: 'off',
                                        }"
                                        :select-size="marginOptions.length"
                                        @update:modelValue="handleMarginInput"
                                    />
                                </div>
                            </div>
                        </div>

                        <div class="field">
                            <div class="field-pair">
                                <div class="field-stack">
                                    <label class="form-label small mb-1">Lead Days</label>
                                    <InputSelectDropdown
                                        v-model="q_lead_time"
                                        input-id="q_lead_time"
                                        select-id="q_lead_select"
                                        input-type="number"
                                        :options="lead_items"
                                        :input-class="
                                            fieldClass('form-control form-control-sm', 'lead_time')
                                        "
                                        :input-attrs="{ style: 'width: 5em', autocomplete: 'off' }"
                                        :select-size="Math.max(lead_items.length, 5)"
                                    />
                                </div>
                                <div class="field-stack">
                                    <label class="form-label small mb-1">Exchange</label>
                                    <div class="form-check inline-checkbox">
                                        <input
                                            id="q_exchange"
                                            v-model="q_exchange"
                                            type="checkbox"
                                            :class="fieldClass('form-check-input', 'exchange')"
                                            name="exchange"
                                            autocomplete="off"
                                        />
                                        <label class="form-check-label" for="q_exchange">Yes</label>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="field full-width">
                            <label class="form-label small mb-1">Core</label>
                            <input
                                id="q_core"
                                v-model="q_core"
                                type="number"
                                :class="fieldClass('form-control form-control-sm', 'core')"
                                name="core"
                                autocomplete="off"
                            />
                        </div>

                        <div class="field">
                            <label class="form-label small mb-1">Charge Type</label>
                            <InputSelectDropdown
                                v-model="q_extra_charge_type"
                                input-id="q_extra_charge_type"
                                select-id="q_charge_type_select"
                                :options="extra_charge_type_items"
                                option-value-key="value"
                                option-label-key="text"
                                :input-class="
                                    fieldClass('form-control form-control-sm', 'extra_charge_type')
                                "
                                :input-attrs="{ style: 'width: 5em', autocomplete: 'off' }"
                                :select-size="Math.max(extra_charge_type_items.length, 5)"
                                :include-empty="false"
                            />
                        </div>

                        <div class="field">
                            <label class="form-label small mb-1">Charge</label>
                            <input
                                id="q_extra_charge"
                                v-model="q_extra_charge"
                                type="number"
                                :class="fieldClass('form-control form-control-sm', 'extra_charge')"
                                name="extra_charge"
                                autocomplete="off"
                            />
                        </div>

                        <div class="field">
                            <label class="form-label small mb-1">
                                Quote
                                <span
                                    v-if="currentBpaMin !== null"
                                    class="quote-modal-note quote-modal-note--warning"
                                >
                                    (Min BPA: {{ currentBpaMin }})
                                </span>
                                <span
                                    v-if="currentGsaMin !== null"
                                    class="quote-modal-note quote-modal-note--info ms-1"
                                >
                                    (Min GSA: {{ currentGsaMin }})
                                </span>
                            </label>
                            <input
                                id="q_quote"
                                v-model="q_quote"
                                type="number"
                                :class="fieldClass('form-control form-control-sm', 'quote')"
                                name="quote"
                                autocomplete="off"
                                @input="handleQuoteInput"
                                @blur="calculateMarginFromQuote()"
                            />
                        </div>

                        <div class="field">
                            <label class="form-label small mb-1">Ext Quote</label>
                            <input
                                id="q_ext_quote"
                                v-model="q_ext_quote"
                                type="number"
                                :class="fieldClass('form-control form-control-sm', 'ext_quote')"
                                name="ext_quote"
                                autocomplete="off"
                                disabled="True"
                            />
                        </div>

                        <div class="field full-width">
                            <label class="form-label small mb-1">Notes</label>
                            <input
                                id="q_notes"
                                v-model="q_notes"
                                type="text"
                                :class="fieldClass('form-control form-control-sm', 'notes')"
                                name="notes"
                                placeholder="Notes..."
                                autocomplete="off"
                            />
                        </div>
                    </div>
                </form>
                <div v-show="!isMinimized" class="columns submit-section">
                    <p v-if="isBpaPart" class="quote-modal-note quote-modal-note--warning">
                        This is part of the BPA agreenment
                    </p>
                    <div v-if="isBpaPart" class="form-check bpa-acknowledge">
                        <input
                            id="bpa_acknowledge"
                            v-model="bpaOverride"
                            type="checkbox"
                            class="form-check-input"
                            name="bpa_override"
                        />
                        <label class="form-check-label" for="bpa_acknowledge">
                            I acknowledge this is a BPA part and want to force submission.
                        </label>
                    </div>
                    <p
                        v-if="isGsaPart && isGsaCustomer"
                        class="quote-modal-note quote-modal-note--info"
                    >
                        This part is under a GSA pricing agreement. If the customer is GSA the quote
                        must match the minimum price.
                    </p>
                    <p
                        v-if="isGsaPart && !isGsaCustomer && !isDlaCustomer"
                        class="quote-modal-note quote-modal-note--info"
                    >
                        This part is under a GSA pricing agreement. If the customer is Non-GSA the quote must at least meet the minimum price.
                    </p>
                    <p
                        v-if="isGsaPart && isDlaCustomer"
                        class="quote-modal-note quote-modal-note--info"
                    >
                        This part is under a GSA pricing agreement. If the customer is DLA it can be
                        quoted for less.
                    </p>
                    <div v-if="isGsaPart && isDlaCustomer" class="form-check bpa-acknowledge">
                        <input
                            id="gsa_acknowledge"
                            v-model="gsaOverride"
                            type="checkbox"
                            class="form-check-input"
                            name="gsa_override"
                        />
                        <label class="form-check-label" for="gsa_acknowledge">
                            I acknowledge this is a GSA part and want to force submission.
                        </label>
                    </div>
                    <div class="column">
                        <button
                            v-if="hasSubmittedQuote"
                            id="quote_update"
                            class="button quote-modal-action quote-modal-action--success"
                            @click="submitQuote(false)"
                            :disabled="isSubmitDisabled || isSubmitting"
                        >
                            Update Quote
                        </button>
                        <button
                            v-else
                            id="quote_submit"
                            class="button quote-modal-action quote-modal-action--success"
                            @click="submitQuote()"
                            :disabled="isSubmitDisabled || isSubmitting"
                        >
                            Submit
                        </button>
                    </div>
                    <div v-if="hasSubmittedQuote" class="column" v-show="false">
                        <button
                            id="quote_submit_new"
                            class="button quote-modal-action quote-modal-action--brand"
                            @click="submitQuote(true)"
                            :disabled="isSubmitDisabled || isSubmitting"
                        >
                            Submit New Quote
                        </button>
                    </div>
                    <div class="column">
                        <button
                            id="quote_none"
                            class="button quote-modal-action quote-modal-action--brand"
                            @click="showNoQuoteModal()"
                            :disabled="isSubmitting"
                        >
                            No Quote
                        </button>
                    </div>
                </div>
            </div>
        </Transition>
    </div>
</template>

<script>
    import QuoteService from '@/services/quotes.service'
    import config from '@/services/config'
    import NoQuoteModal from '@/components/modals/NoQuoteModal.vue'
    import InputSelectDropdown from '@/components/shared/InputSelectDropdown.vue'
    import { Modal } from 'bootstrap'
    import { mapStores } from 'pinia'
    import { useDropdownsStore } from '@/stores/dropdownsStore'
    import { useSearchStore } from '@/stores/searchStore'
    import { useSubmitQuoteModalStore } from '@/stores/submitQuoteModalStore'
    import {
        descriptionText,
        toNonNegativeNumberOrNull,
        toPositiveNumberOrNull,
    } from '@/helpers/string'
    import {
        getQuoteCoreDeposit,
        getQuoteListPrice,
        getQuoteUnitCost,
    } from '@/utils/vendorRow'

    export default {
        name: 'QuoteModal',
        components: {
            NoQuoteModal,
            InputSelectDropdown,
        },
        data() {
            return {
                backendURL: process.env.VUE_APP_BACKEND_URL,
                invalidFields: [],
                isMinimized: false,
                showModal: true,
                isSubmitting: false,
                q_part_selected: '',
                q_part_quoted: '',
                q_description: '',
                q_vendor: '',
                q_condition: '',
                q_unit_cost: '',
                q_quantity: '',
                q_list_price: '',
                q_unit_of_measure: '',
                q_margin: '',
                q_lead_time: '',
                q_exchange: '',
                q_core: '',
                q_extra_charge: '',
                q_extra_charge_type: '',
                q_quote: '',
                q_ext_quote: '',
                q_notes: '',
                isSyncingQuote: false,
                isDerivingMarginFromQuote: false,
                pricingInputSource: null,
                bpaOverride: false,
                gsaOverride: false,
                uom_items: ['Each', 'Foot'],
                lead_items: [0, 3, 5, 15, 30, 90],
                extra_charge_type_items: [
                    { value: '', text: '', disabled: true, selected: true },
                    { value: 'CC Fees', text: 'CC Fees' },
                    { value: 'Expedite Fees', text: 'Expedite Fees' },
                    { value: 'MFG Cert Fees', text: 'MFG Cert Fees' },
                    { value: 'Multiple', text: 'Multiple' },
                    { value: 'Other', text: 'Other' },
                    { value: 'Shipping', text: 'Shipping' },
                ],
                qNoQuoteReason: '',
                gsaMinPrice: null,
            }
        },
        props: {
            parts_and_quantities: {
                type: Array,
                default: () => [],
            },
            selected_tab: {
                type: String,
                default: '',
            },
            isBpaNoquote: {
                type: Boolean,
                default: false,
            },
            quoteid: {
                type: Number,
                required: false,
                default: null,
            },
            searchactionid: {
                type: Number,
                required: true,
            },
            data: {
                type: Object,
                default: () => {},
            },
        },
        computed: {
            ...mapStores(useDropdownsStore, useSearchStore, useSubmitQuoteModalStore),
            isFromMarketplaceParts() {
                return this.selected_tab === 'Marketplace Parts'
            },
            selectedPartFromStore() {
                return this.searchStore?.getSelectedPart || ''
            },
            activeSelectedPart() {
                return this.selectedPartFromStore || ''
            },
            activeSelectedPartIndex() {
                return this.normalizeQuoteIndex(this.searchStore?.getSelectedPartIndex)
            },
            // Preferred modal identity when repeated searched rows share the same part number.
            activeSelectedQuoteId() {
                return this.normalizeQuoteId(this.searchStore?.getSelectedQuoteId)
            },
            quotesForSelectedPart() {
                const activePartKey = this.normalizePartKey(this.activeSelectedPart)
                const activePartIndex = this.activeSelectedPartIndex
                const activeQuoteId = this.activeSelectedQuoteId
                if (!activePartKey) {
                    return []
                }

                return (this.submitQuoteModalStore.getQuotes || [])
                    .filter((value) => {
                        // Prefer quote ID, then row index, then requested part fallback for legacy rows.
                        const quoteId = this.normalizeQuoteId(value?.id)
                        if (activeQuoteId !== null && quoteId !== null) {
                            return quoteId === activeQuoteId
                        }

                        const selectedPartIndex = this.normalizeQuoteIndex(
                            value?.selected_part_index
                        )
                        if (selectedPartIndex !== null && activePartIndex !== null) {
                            return selectedPartIndex === activePartIndex
                        }

                        const requestedPartKey = this.normalizePartKey(value?.part_number)
                        if (requestedPartKey) {
                            return requestedPartKey === activePartKey
                        }

                        return this.normalizePartKey(value?.part_quoted) === activePartKey
                    })
                    .sort((left, right) => (left?.id || 0) - (right?.id || 0))
            },
            submittedQuoteForSelectedPart() {
                const submittedQuotes = this.quotesForSelectedPart.filter(
                    (quote) => quote?.submitted
                )
                return submittedQuotes.length > 0
                    ? submittedQuotes[submittedQuotes.length - 1]
                    : null
            },
            draftQuoteForSelectedPart() {
                const draftQuotes = this.quotesForSelectedPart.filter((quote) => !quote?.submitted)
                return draftQuotes.length > 0 ? draftQuotes[draftQuotes.length - 1] : null
            },
            editableQuoteRecord() {
                return this.submittedQuoteForSelectedPart || this.draftQuoteForSelectedPart
            },
            hasSubmittedQuote() {
                return Boolean(this.submittedQuoteForSelectedPart)
            },
            isRetail() {
                return config.getSubdomain && config.getSubdomain() === 'retail'
            },
            conditionCodeOptions() {
                return Array.isArray(this.dropdownsStore?.condition_codes)
                    ? this.dropdownsStore.condition_codes
                    : []
            },
            vendorOptions() {
                const options = Array.isArray(this.dropdownsStore?.vendor)
                    ? [...this.dropdownsStore.vendor]
                    : []
                const extraOptions = [
                    this.q_vendor,
                    this.data?.vendor,
                    this.data?.inventory_source,
                ]

                extraOptions.forEach((option) => {
                    const normalizedOption = String(option || '').trim()
                    if (!normalizedOption) {
                        return
                    }
                    const hasOption = options.some(
                        (item) =>
                            String(item || '').trim().toLowerCase() ===
                            normalizedOption.toLowerCase()
                    )
                    if (!hasOption) {
                        options.push(normalizedOption)
                    }
                })

                return options
            },
            marginOptions() {
                return ['6', '7', '8', '9', '10', '11', '12']
            },
            activeCustomer() {
                return this.searchStore?.getCustomer || ''
            },
            activeCustomerRef() {
                return this.searchStore?.getCustomerRef || ''
            },
            activeSearchType() {
                return this.searchStore?.getQuoteType || ''
            },
            bpaParts() {
                const parts = this.searchStore?.getBpaParts || []
                return Array.isArray(parts) ? parts : []
            },
            bpaMinimums() {
                return this.searchStore?.getBpaMinimums || {}
            },
            gsaParts() {
                const parts = this.searchStore?.getGsaParts || []
                return Array.isArray(parts) ? parts : []
            },
            gsaMinimums() {
                return this.searchStore?.getGsaMinimums || {}
            },
            quotedPartCandidates() {
                const quotedPart = String(this.q_part_quoted || '').trim()
                return quotedPart ? [quotedPart] : []
            },
            normalizedQuotedPartCandidates() {
                return this.quotedPartCandidates
                    .map((part) => this.normalizePartKey(part))
                    .filter(Boolean)
            },
            gsaMinimumEntry() {
                const minimums =
                    this.gsaMinimums && typeof this.gsaMinimums === 'object'
                        ? this.gsaMinimums
                        : {}
                const candidates = this.quotedPartCandidates

                for (const part of candidates) {
                    if (Object.prototype.hasOwnProperty.call(minimums, part)) {
                        return minimums[part]
                    }
                }

                const normalizedCandidates = new Set(this.normalizedQuotedPartCandidates)
                const matchedKey = Object.keys(minimums).find((key) =>
                    normalizedCandidates.has(this.normalizePartKey(key))
                )

                return matchedKey ? minimums[matchedKey] : null
            },
            currentBpaMin() {
                const part = this.activeSelectedPart
                const min = this.bpaMinimums?.[part]
                return min === undefined ? null : min
            },
            currentGsaMin() {
                return this.gsaMinPrice === undefined ? null : this.gsaMinPrice
            },
            isBpaPart() {
                const activePart = this.activeSelectedPart
                if (!activePart) {
                    return false
                }
                return this.bpaParts.some(
                    (part) =>
                        part &&
                        part.toString().toLowerCase().trim() ===
                            activePart.toString().toLowerCase().trim()
                )
            },
            isGsaPart() {
                if (this.normalizedQuotedPartCandidates.length === 0) {
                    return false
                }

                const activeCandidates = new Set(this.normalizedQuotedPartCandidates)
                const hasGsaPart = this.gsaParts.some((part) =>
                    activeCandidates.has(this.normalizePartKey(part))
                )

                return hasGsaPart || Boolean(this.gsaMinimumEntry)
            },
            isGsaCustomer() {
                return (
                    this.activeCustomer &&
                    this.activeCustomer.toString().trim().toLowerCase() === 'gsa'
                )
            },
            isDlaCustomer() {
                return (
                    this.activeCustomer &&
                    this.activeCustomer.toString().trim().toLowerCase() === 'defense & logistics agency'
                )
            },
            headerCustomer() {
                return this.activeCustomer || 'Customer'
            },
            searchTypeBadge() {
                return this.activeSearchType ? this.activeSearchType : ''
            },
            isSubmitDisabled() {
                if (this.isBpaPart) {
                    return !this.bpaOverride
                }
                if (this.isGsaPart) {
                    const hasGsaMin =
                        this.currentGsaMin !== null && this.currentGsaMin !== undefined
                    if (this.isGsaCustomer && hasGsaMin) {
                        return Number(this.q_quote) !== Number(this.currentGsaMin)
                    }
                    const belowMin =
                        this.currentGsaMin !== null &&
                        this.currentGsaMin !== undefined &&
                        this.q_quote !== null &&
                        this.q_quote !== undefined &&
                        Number(this.q_quote) < Number(this.currentGsaMin)
                    if (!this.isDlaCustomer && belowMin) {
                        return true
                    }
                    if (this.isDlaCustomer && belowMin) {
                        return !this.gsaOverride
                    }
                }
                return this.isBpaNoquote
            },
        },
        watch: {
            q_quantity: function () {
                this.recalculateExtQuote()
                this.recalculateMinGSAPrice()
            },
            q_part_quoted: function () {
                this.recalculateMinGSAPrice()
            },
            q_quote: function () {
                this.recalculateExtQuote()
            },
            q_margin: function () {
                if (
                    !this.isSyncingQuote &&
                    !this.isDerivingMarginFromQuote &&
                    this.pricingInputSource === 'margin'
                ) {
                    this.calculateQuote()
                }
                this.recalculateExtQuote()
            },
            q_unit_cost: function () {
                if (this.isSyncingQuote) {
                    return
                }
                if (this.pricingInputSource === 'margin') {
                    this.calculateQuote()
                    return
                }
                if (this.pricingInputSource === 'quote') {
                    this.calculateMarginFromQuote()
                }
            },
            selectedPartFromStore() {
                this.syncQuote()
                this.recalculateMinGSAPrice()
            },
            activeSelectedPartIndex() {
                this.syncQuote()
                this.recalculateMinGSAPrice()
            },
            activeSelectedQuoteId() {
                this.syncQuote()
                this.recalculateMinGSAPrice()
            },
            activeCustomer() {
                this.recalculateMinGSAPrice()
            },
            gsaMinimums: {
                handler() {
                    this.recalculateMinGSAPrice()
                },
                deep: true,
            },
            parts_and_quantities: {
                handler() {
                    this.syncQuantity()
                    this.recalculateMinGSAPrice()
                },
                deep: true,
            },
            editableQuoteRecord: {
                handler() {
                    this.syncQuote()
                    this.recalculateMinGSAPrice()
                },
                deep: true,
            },
            data: {
                handler() {
                    this.syncQuote()
                    this.recalculateMinGSAPrice()
                },
                deep: true,
            },
        },
        mounted() {
            this.syncQuote()
            this.recalculateMinGSAPrice()
        },
        methods: {
            descriptionText,
            normalizePartKey(value) {
                return String(value ?? '')
                    .trim()
                    .toUpperCase()
                    .replace(/[^A-Z0-9]+/g, '')
            },
            normalizeQuoteIndex(value) {
                if (value === '' || value === null || value === undefined) {
                    return null
                }
                const parsed = Number(value)
                return Number.isInteger(parsed) && parsed >= 0 ? parsed : null
            },
            normalizeQuoteId(value) {
                if (value === '' || value === null || value === undefined) {
                    return null
                }
                const parsed = Number(value)
                return Number.isInteger(parsed) && parsed > 0 ? parsed : null
            },
            normalizeLookupValue(value) {
                return String(value ?? '')
                    .trim()
                    .toUpperCase()
                    .replace(/[^A-Z0-9]+/g, ' ')
                    .replace(/\s+/g, ' ')
                    .trim()
            },
            getCanonicalUom(rawValue) {
                if (rawValue === null || rawValue === undefined || rawValue === '') {
                    return ''
                }
                const normalized = this.normalizeLookupValue(rawValue)
                const uomAliases = {
                    EA: 'Each',
                    EACH: 'Each',
                    FT: 'Foot',
                    FOOT: 'Foot',
                }
                const mapped = uomAliases[normalized] || ''
                return mapped || String(rawValue).trim()
            },
            getPreferredTextValue(...values) {
                const preferred = values.find((value) => {
                    if (value === null || value === undefined) {
                        return false
                    }
                    return String(value).trim() !== ''
                })

                return preferred == null ? '' : String(preferred)
            },
            getFormFieldValue(value) {
                return value === null || value === undefined ? '' : value
            },
            syncQuote() {
                this.isSyncingQuote = true
                const hasPrefillData =
                    this.data && typeof this.data === 'object' && Object.keys(this.data).length > 0
                const quoteData = hasPrefillData
                    ? { ...(this.editableQuoteRecord || {}), ...this.data }
                    : { ...this.data, ...(this.editableQuoteRecord || {}) }
                const usePrefillPriceValues =
                    hasPrefillData && this.data?.use_prefill_price_values === true

                const selected = this.activeSelectedPart
                const preferredDescription = this.getPreferredTextValue(
                    hasPrefillData ? this.data?.description : this.editableQuoteRecord?.description,
                    hasPrefillData ? this.editableQuoteRecord?.description : this.data?.description
                )
                this.bpaOverride = false
                this.gsaOverride = false
                this.q_part_selected = selected
                this.q_part_quoted = quoteData.part_quoted || quoteData.part_number || selected
                this.q_description = descriptionText(preferredDescription)
                this.q_vendor = this.getPreferredTextValue(
                    quoteData?.vendor,
                    quoteData?.ui_vendor_name,
                    this.isFromMarketplaceParts ? quoteData?.manufacturer_name : ''
                )
                this.q_condition = this.getPreferredTextValue(
                    quoteData?.condition,
                    this.data?.condition
                )
                this.q_unit_of_measure = this.getCanonicalUom(
                    quoteData?.unit_of_measure || quoteData?.uom
                )
                this.q_margin = this.getFormFieldValue(quoteData?.margin)
                this.q_lead_time = this.parseIntOrNull(quoteData?.lead_time)
                this.q_exchange = quoteData?.exchange || false
                const quoteCoreDeposit = hasPrefillData
                    ? getQuoteCoreDeposit(this.data)
                    : null
                this.q_core =
                    quoteCoreDeposit != null
                        ? quoteCoreDeposit
                        : quoteData?.core !== null && quoteData?.core !== undefined
                          ? quoteData.core
                          : ''
                this.q_extra_charge_type = quoteData?.extra_charge_type || ''
                this.q_extra_charge = quoteData?.extra_charge || ''
                this.q_quote = this.getFormFieldValue(quoteData?.quote)
                this.q_ext_quote = quoteData?.ext_quote || 0
                const syncedQuote = toPositiveNumberOrNull(this.q_quote)
                const syncedMargin = toNonNegativeNumberOrNull(this.q_margin)
                this.pricingInputSource =
                    syncedQuote !== null ? 'quote' : syncedMargin !== null ? 'margin' : null
                const quoteUnitCost = hasPrefillData
                    ? usePrefillPriceValues
                        ? this.data?.unit_cost
                        : getQuoteUnitCost(this.data)
                    : null
                const quoteListPrice = hasPrefillData
                    ? usePrefillPriceValues
                        ? this.data?.list_price
                        : getQuoteListPrice(this.data)
                    : null
                this.q_unit_cost =
                    quoteUnitCost != null
                        ? quoteUnitCost
                        : quoteData.unit_cost !== null && quoteData.unit_cost !== undefined
                          ? quoteData.unit_cost
                          : ''
                this.q_list_price =
                    quoteListPrice != null
                        ? quoteListPrice
                        : quoteData.list_price !== null && quoteData.list_price !== undefined
                          ? quoteData.list_price
                          : ''
                this.q_notes = quoteData.notes || ''
                const persistedQuantity = this.parseIntOrNull(this.editableQuoteRecord?.quantity)
                if (persistedQuantity !== null && persistedQuantity > 0) {
                    this.q_quantity = persistedQuantity
                } else {
                    this.syncQuantity()
                }
                this.recalculateExtQuote()
                this.recalculateMinGSAPrice()
                this.$nextTick(() => {
                    this.isSyncingQuote = false
                })
            },
            handleMarginInput(value) {
                const margin = toNonNegativeNumberOrNull(value)
                this.pricingInputSource =
                    margin !== null
                        ? 'margin'
                        : toPositiveNumberOrNull(this.q_quote) !== null
                          ? 'quote'
                          : null
                this.q_margin = value
            },
            handleQuoteInput(event) {
                const quote = toPositiveNumberOrNull(event?.target?.value ?? this.q_quote)
                this.pricingInputSource = quote !== null ? 'quote' : null
                this.recalculateExtQuote()
            },
            syncQuantity() {
                const persistedQuantity = this.parseIntOrNull(this.editableQuoteRecord?.quantity)
                if (persistedQuantity !== null && persistedQuantity > 0) {
                    this.q_quantity = persistedQuantity
                    return
                }

                const selected = this.activeSelectedPart
                const found = (this.parts_and_quantities || []).find(
                    (item) => item.part === selected
                )
                const parsedQuantity = found ? parseInt(found.quantity, 10) : NaN
                this.q_quantity =
                    Number.isFinite(parsedQuantity) && parsedQuantity > 0 ? parsedQuantity : 1
            },
            parseIntOrNull(val) {
                return val === '' || val === null || val === undefined ? null : parseInt(val, 10)
            },
            parseFloatOrNull(val) {
                return val === '' || val === null || val === undefined ? null : parseFloat(val)
            },
            calculateMarginFromQuote() {
                const replacementCost = toPositiveNumberOrNull(this.q_unit_cost)
                const margin = toNonNegativeNumberOrNull(this.q_margin)
                const quote = toPositiveNumberOrNull(this.q_quote)
                if (replacementCost === null || quote === null) {
                    return
                }
                const calculatedQuote =
                    margin === null || margin >= 100 ? null : replacementCost / (1 - margin / 100)
                const isQuoteInvalid = quote !== calculatedQuote

                if (margin === null || (isQuoteInvalid && replacementCost !== null)) {
                    this.isDerivingMarginFromQuote = true
                    this.q_margin = (((quote - replacementCost) / quote) * 100).toFixed(2)
                    this.$nextTick(() => {
                        this.isDerivingMarginFromQuote = false
                    })
                }
            },
            calculateQuote() {
                const replacementCost = toPositiveNumberOrNull(this.q_unit_cost)
                const margin = toNonNegativeNumberOrNull(this.q_margin)
                if (replacementCost === null || margin === null || margin >= 100) {
                    this.q_quote = ''
                    return
                }

                this.q_quote = (replacementCost / (1 - margin / 100)).toFixed(2)
            },
            recalculateExtQuote() {
                const quantityValue = toPositiveNumberOrNull(this.q_quantity)
                const quoteValue = toPositiveNumberOrNull(this.q_quote)
                if (quantityValue === null || quoteValue === null) {
                    this.q_ext_quote = ''
                    return
                }

                this.q_ext_quote = (quantityValue * quoteValue).toFixed(2)
            },
            recalculateMinGSAPrice() {
                if (!this.isGsaPart) {
                    // case is not gsa part, skip min price rule.
                    this.gsaMinPrice = null
                    return
                }

                if (this.isGsaCustomer) {
                    // case is GSA part and GSA customer
                    this.gsaMinPrice = this.findMinPriceByQuantity(this.q_quantity)
                } else {
                    // case is GSA part but not GSA customer, fetch first range, quantity === 1
                    this.gsaMinPrice = this.findMinPriceByQuantity(1)
                }
            },
            parseGsaPrice(value) {
                if (value === null || value === undefined || value === '') {
                    return null
                }
                if (typeof value === 'number') {
                    return Number.isFinite(value) ? value : null
                }

                const parsed = parseFloat(String(value).replace(/,/g, ''))
                return Number.isFinite(parsed) ? parsed : null
            },
            findMinPriceByQuantity(quantity) {
                const gsaMiniumPriceRanges = this.gsaMinimumEntry
                if (!gsaMiniumPriceRanges || typeof gsaMiniumPriceRanges !== 'object') {
                    return null
                }

                const basePrice = this.parseGsaPrice(gsaMiniumPriceRanges.price)
                const quantityRanges = gsaMiniumPriceRanges.quantity_ranges
                const parsedQuantity = parseInt(quantity, 10)
                const quoteQuantity =
                    Number.isFinite(parsedQuantity) && parsedQuantity > 0 ? parsedQuantity : 1

                if (!quantityRanges || typeof quantityRanges !== 'object') {
                    return basePrice
                }

                for (const range of Object.values(quantityRanges)) {
                    if (!range) {
                        continue
                    }
                    const min = parseInt(range.min, 10)
                    const max = parseInt(range.max, 10)
                    if (
                        Number.isFinite(min) &&
                        Number.isFinite(max) &&
                        quoteQuantity >= min &&
                        quoteQuantity <= max
                    ) {
                        return this.parseGsaPrice(range.price) ?? basePrice
                    }
                }
                return basePrice
            },
            validateRequiredQuoteFields() {
                const invalid = []
                const isBlank = (value) => !String(value ?? '').trim()

                if (isBlank(this.q_vendor)) invalid.push('vendor')
                if (this.isRetail && isBlank(this.q_condition)) invalid.push('condition')
                if (toPositiveNumberOrNull(this.q_quantity) === null) invalid.push('quantity')
                if (toPositiveNumberOrNull(this.q_unit_cost) === null) invalid.push('unit_cost')
                if (toPositiveNumberOrNull(this.q_list_price) === null) invalid.push('list_price')
                if (isBlank(this.q_unit_of_measure)) invalid.push('unit_of_measure')
                const parsedMargin = toNonNegativeNumberOrNull(this.q_margin)
                const hasMargin = parsedMargin !== null
                const hasQuote = toPositiveNumberOrNull(this.q_quote) !== null
                if (parsedMargin !== null && parsedMargin >= 100) {
                    invalid.push('margin')
                }
                if (!hasQuote && !hasMargin) {
                    invalid.push('margin')
                    invalid.push('quote')
                }

                this.invalidFields = invalid
                if (invalid.length > 0) {
                    this.q_ext_quote = ''
                    return false
                }

                this.recalculateExtQuote()
                return toPositiveNumberOrNull(this.q_ext_quote) !== null
            },
            getSubmittedPartValues() {
                const partNumber = String(this.activeSelectedPart || '').trim()
                const quotedCandidate = this.q_part_quoted ? this.q_part_quoted : partNumber
                const partQuoted = String(quotedCandidate || '').trim() || partNumber
                return { partNumber, partQuoted }
            },
            fieldClass(base, fieldKey) {
                return [base, this.invalidFields.includes(fieldKey) ? 'is-invalid' : '']
            },
            formatQuoteSubmitError(err, fallbackMessage) {
                const lines = [fallbackMessage]
                const backendMessage = err?.backendError || err?.message

                if (backendMessage && backendMessage !== fallbackMessage) {
                    lines.push(`Message: ${backendMessage}`)
                }

                if (err?.status) {
                    lines.push(`Status: ${err.status}`)
                }

                return lines.join('\n')
            },
            async handleNoQuoteReason(reason) {
                this.q_description = descriptionText(this.q_description)
                this.qNoQuoteReason = reason
                this.isSubmitting = true
                try {
                    const { partNumber, partQuoted } = this.getSubmittedPartValues()
                    const payload = {
                        id: this.editableQuoteRecord?.id ?? null,
                        condition: this.q_condition || null,
                        core: this.parseFloatOrNull(this.q_core),
                        customer: this.activeCustomer,
                        customer_ref: this.activeCustomerRef,
                        description: this.q_description,
                        exchange: this.q_exchange,
                        quote: this.parseFloatOrNull(this.q_quote),
                        ext_quote: null,
                        extra_charge: this.parseFloatOrNull(this.q_extra_charge),
                        extra_charge_type: this.q_extra_charge_type,
                        lead_time: this.parseIntOrNull(this.q_lead_time),
                        list_price: this.parseFloatOrNull(this.q_list_price),
                        margin: this.parseFloatOrNull(this.q_margin),
                        margin_input: this.parseFloatOrNull(this.margin_input),
                        no_quote: true,
                        no_quote_reason: reason,
                        notes: this.q_notes,
                        part_number: partNumber,
                        // Preserve row identity through submit responses for next-row navigation.
                        selected_part_index: this.activeSelectedPartIndex,
                        part_quoted: partQuoted,
                        quantity: this.parseIntOrNull(this.q_quantity),
                        quote_type: this.activeSearchType || 'Quote',
                        sales_order_id: null,
                        search_action_id: this.searchactionid,
                        submitted: true,
                        unit_cost: this.parseFloatOrNull(this.q_unit_cost),
                        unit_of_measure: this.q_unit_of_measure,
                        vendor: this.q_vendor,
                    }
                    const data = await QuoteService.submitQuote(payload)
                    const persistedQuote = data?.data
                        ? {
                              ...data.data,
                              selected_part_index: payload.selected_part_index,
                          }
                        : null
                    this.submitQuoteModalStore.upsertQuote(persistedQuote)
                    const persistedPayload = {
                        ...(data?.payload || payload),
                        selected_part_index: payload.selected_part_index,
                    }
                    const form = {
                        part_selected: partNumber,
                        selected_part_index: this.activeSelectedPartIndex,
                        q_part_quoted: partQuoted,
                        q_description: this.q_description,
                        q_vendor: this.q_vendor,
                        q_condition: this.q_condition,
                        q_unit_cost: this.q_unit_cost,
                        q_quantity: this.q_quantity,
                        q_list_price: this.q_list_price,
                        q_unit_of_measure: this.q_unit_of_measure,
                        q_margin: this.q_margin,
                        q_lead_time: this.q_lead_time,
                        q_exchange: this.q_exchange,
                        q_core: this.q_core,
                        q_extra_charge: this.q_extra_charge,
                        q_extra_charge_type: this.q_extra_charge_type,
                        q_quote: null,
                        q_ext_quote: null,
                        q_notes: this.q_notes,
                        q_no_quote_reason: reason,
                    }
                    this.closeModal()
                    this.$emit('quote-submitted', { data, payload: persistedPayload, form })
                } catch (err) {
                    const message = this.formatQuoteSubmitError(
                        err,
                        'Failed to submit no quote.'
                    )
                    this.invalidFields = Array.isArray(err?.fields) ? err.fields : []
                    alert(message)
                } finally {
                    this.isSubmitting = false
                }
            },
            showNoQuoteModal() {
                const modal = new Modal(document.getElementById('noQuoteModal'))
                modal.show()
            },
            closeModal() {
                const modalElement = document.getElementById('noQuoteModal')
                if (modalElement) {
                    const modal = Modal.getInstance(modalElement) || new Modal(modalElement)
                    modal.hide()
                }
            },
            edit_quote() {
                this.recalculateExtQuote()
            },
            toggleMinimize() {
                this.isMinimized = !this.isMinimized
            },
            maximize() {
                this.isMinimized = false
            },
            hideQuote() {
                this.isMinimized = false
                this.showModal = false
            },
            emitHide() {
                this.$emit('hide-quote')
            },
            async submitQuote(createNew = false) {
                this.q_description = descriptionText(this.q_description)
                this.invalidFields = []
                const isValid = this.validateRequiredQuoteFields()
                if (!isValid) {
                    const requiredFieldsMessage = this.isRetail
                        ? 'Vendor, Condition Code, Quantity, Replacement Cost, List Price, UOM, and either Quote or Margin are required to submit.'
                        : 'Vendor, Quantity, Replacement Cost, List Price, UOM, and either Quote or Margin are required to submit.'
                    alert(requiredFieldsMessage)
                    return
                }
                this.isSubmitting = true
                const minPrice = this.bpaMinimums?.[this.activeSelectedPart]
                if (
                    minPrice !== undefined &&
                    minPrice !== null &&
                    this.q_quote !== null &&
                    this.q_quote !== undefined &&
                    Number(this.q_quote) < Number(minPrice)
                ) {
                    alert(
                        `BPA minimum price for ${this.activeSelectedPart} is ${minPrice}. Please quote at or above this amount.`
                    )
                    this.isSubmitting = false
                    return
                }
                const gsaMin = this.gsaMinPrice
                const isGsaPart = this.isGsaPart
                const isDla = this.isDlaCustomer
                const quoteId = createNew
                    ? null
                    : this.hasSubmittedQuote
                      ? this.submittedQuoteForSelectedPart?.id
                      : this.draftQuoteForSelectedPart?.id
                const { partNumber, partQuoted } = this.getSubmittedPartValues()
                if (
                    isGsaPart &&
                    gsaMin !== undefined &&
                    gsaMin !== null &&
                    this.q_quote !== null &&
                    this.q_quote !== undefined
                ) {
                    if (this.isGsaCustomer && Number(this.q_quote) !== Number(gsaMin)) {
                        alert(
                            `This is a GSA part and the customer is GSA. Quote must match the minimum price of ${gsaMin}.`
                        )
                        this.isSubmitting = false
                        return
                    }
                    if (!isDla && Number(this.q_quote) < Number(gsaMin)) {
                        alert(
                            `GSA minimum price for ${this.activeSelectedPart} is ${gsaMin}. Quote must be at or above this amount.`
                        )
                        this.isSubmitting = false
                        return
                    }
                    if (isDla && Number(this.q_quote) < Number(gsaMin) && !this.gsaOverride) {
                        alert(
                            `This is a GSA part. Check the force update box to submit below the minimum for DLA. Minimum: ${gsaMin}.`
                        )
                        this.isSubmitting = false
                        return
                    }
                }
                const payload = {
                    id: quoteId ?? null,
                    condition: this.q_condition || null,
                    core: this.parseFloatOrNull(this.q_core),
                    customer: this.activeCustomer,
                    customer_ref: this.activeCustomerRef,
                    description: this.q_description,
                    exchange: this.q_exchange,
                    quote: this.parseFloatOrNull(this.q_quote),
                    extra_charge: this.parseFloatOrNull(this.q_extra_charge),
                    extra_charge_type: this.q_extra_charge_type,
                    lead_time: this.q_lead_time,
                    list_price: this.parseFloatOrNull(this.q_list_price),
                    margin: this.q_margin,
                    margin_input: this.margin_input,
                    no_quote: false,
                    no_quote_reason: null,
                    notes:
                        this.q_notes && this.q_notes.toString().trim()
                            ? this.q_notes.toString().trim()
                            : null,
                    part_number: partNumber,
                    // Preserve row identity through submit responses for next-row navigation.
                    selected_part_index: this.activeSelectedPartIndex,
                    part_quoted: partQuoted,
                    quantity: this.q_quantity,
                    quote_type: this.activeSearchType || 'Quote',
                    sales_order_id: null,
                    search_action_id: this.searchactionid,
                    submitted: true,
                    unit_cost: this.parseFloatOrNull(this.q_unit_cost),
                    unit_of_measure: this.q_unit_of_measure,
                    vendor: this.q_vendor,
                }
                try {
                    this.invalidFields = []
                    const data = await QuoteService.submitQuote(payload)
                    const persistedQuote = data?.data
                        ? {
                              ...data.data,
                              selected_part_index: payload.selected_part_index,
                          }
                        : null
                    this.submitQuoteModalStore.upsertQuote(persistedQuote)
                    const persistedPayload = {
                        ...(data?.payload || payload),
                        selected_part_index: payload.selected_part_index,
                    }
                    const form = {
                        part_selected: partNumber,
                        selected_part_index: this.activeSelectedPartIndex,
                        q_part_quoted: partQuoted,
                        q_description: this.q_description,
                        q_vendor: this.q_vendor,
                        q_condition: this.q_condition,
                        q_unit_cost: this.q_unit_cost,
                        q_quantity: this.q_quantity,
                        q_list_price: this.q_list_price,
                        q_unit_of_measure: this.q_unit_of_measure,
                        q_margin: this.q_margin,
                        q_lead_time: this.q_lead_time,
                        q_exchange: this.q_exchange,
                        q_core: this.q_core,
                        q_extra_charge: this.q_extra_charge,
                        q_extra_charge_type: this.q_extra_charge_type,
                        q_quote: this.q_quote,
                        q_ext_quote: this.q_ext_quote,
                        q_notes: this.q_notes,
                    }
                    this.$emit('quote-submitted', { data, payload: persistedPayload, form })
                } catch (err) {
                    const message = this.formatQuoteSubmitError(
                        err,
                        'Failed to submit quote.'
                    )
                    this.invalidFields = Array.isArray(err?.fields) ? err.fields : []
                    alert(message)
                } finally {
                    this.isSubmitting = false
                }
            },
        },
    }
</script>

<style>
    .quote_modal {
        position: fixed;
        z-index: 9999;
        bottom: 0px;
        right: 10px;
        width: min(720px, 90vw);
        padding: 12px;
        background-color: var(--color-surface-canvas);
        color: var(--color-text-primary);
        border: 1px solid var(--color-border-default);
        border-radius: 1em;
        max-height: calc(100vh - 64px);
        overflow-y: auto;
        transition:
            width 1s,
            height 1s;
        box-shadow: var(--shadow-lg);
    }

    .quote_modal.minimized {
        width: auto;
        max-height: none;
        overflow: visible;
        padding: 8px 12px;
    }

    .quote-modal-header {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        padding: 10px 0;
    }

    .quote-modal-header h5 {
        flex: 1;
        margin: 0;
        color: var(--color-text-primary);
    }

    .minimize-btn {
        padding: 0.25rem 0.5rem;
        height: 19px;
    }

    .quote-modal-badge {
        display: inline-flex;
        align-items: center;
        padding: 0.2rem 0.55rem;
        border-radius: var(--radius-pill);
        background: var(--color-brand-primary);
        color: var(--color-text-inverse);
        font-size: 0.72rem;
        font-weight: 700;
        letter-spacing: 0.04em;
    }

    .quote-modal-icon-button {
        background: var(--color-surface-subtle);
        color: var(--color-text-primary);
        border: 1px solid var(--color-border-default);
        box-shadow: none;
    }

    .quote-modal-icon-button:hover,
    .quote-modal-icon-button:focus {
        border-color: var(--color-border-accent-strong);
        color: var(--color-text-primary);
        box-shadow: var(--shadow-focus-brand);
    }

    .quote-form-grid {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        column-gap: 0.75rem;
        row-gap: 0.5rem;
    }

    .quote-form-grid .full-width {
        grid-column: 1 / -1;
    }

    .field-pair {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 0.75rem;
    }

    .field-stack {
        display: flex;
        flex-direction: column;
    }

    .inline-checkbox {
        display: flex;
        align-items: center;
        gap: 0.35rem;
        padding-top: 0.35rem;
    }

    .quote-modal-fade-enter-active,
    .quote-modal-fade-leave-active {
        transition:
            opacity 0.2s ease,
            transform 0.2s ease;
    }

    .quote-modal-fade-enter-from,
    .quote-modal-fade-leave-to {
        opacity: 0;
        transform: translateY(8px);
    }

    .column {
        display: block;
        flex-basis: 0;
        flex-grow: 1;
        flex-shrink: 1;
        padding: 0.25rem;
    }

    .quote_modal form {
        padding: 0 0 12px 0;
        overflow: visible;
    }

    .quote_modal .form-label.small,
    .quote_modal h5,
    .quote_modal p,
    .quote_modal label,
    .quote_modal .form-check-label {
        color: var(--color-text-primary);
        font-weight: bold;
    }

    .quote_modal .form-control,
    .quote_modal .form-control-sm,
    .quote_modal .form-select,
    .quote_modal .form-select-sm,
    .quote_modal .form-check-input {
        background-color: var(--color-surface-subtle);
        color: var(--color-text-primary);
        border-color: var(--color-border-default);
    }

    .quote_modal .form-select option {
        background-color: var(--color-surface-subtle);
        color: var(--color-text-primary);
    }

    .quote_modal .form-control:disabled,
    .quote_modal .form-control-sm:disabled,
    .quote_modal .form-select:disabled,
    .quote_modal .form-select-sm:disabled {
        background: var(--color-surface-subtle);
        color: var(--color-text-muted);
        opacity: 0.9;
    }

    .quote_modal .form-control::placeholder {
        color: var(--color-text-muted);
    }

    .quote_modal .form-control:focus,
    .quote_modal .form-select:focus,
    .quote_modal .form-check-input:focus {
        border-color: var(--color-border-accent-strong);
        box-shadow: var(--shadow-focus-brand);
    }

    .quote_modal .form-check-input:checked {
        background-color: var(--color-brand-primary);
        border-color: var(--color-brand-primary);
    }

    .quote_modal .is-invalid {
        border-color: var(--color-border-accent-strong) !important;
        box-shadow: var(--shadow-focus-brand) !important;
    }

    .quote_modal .delete {
        background-color: var(--color-neutral-soft);
    }

    .quote_modal .delete::before,
    .quote_modal .delete::after {
        background-color: var(--color-text-primary);
    }

    .quote-modal-note {
        font-weight: 700;
    }

    .quote-modal-note--warning {
        color: var(--color-state-warning) !important;
    }

    .quote-modal-note--info {
        color: var(--color-state-info) !important;
    }

    .quote-modal-action {
        width: 100%;
        border: 1px solid transparent;
        color: var(--color-text-inverse);
        font-weight: 700;
        transition:
            transform 0.15s ease,
            opacity 0.15s ease;
    }

    .quote-modal-action:hover:not(:disabled) {
        transform: translateY(-1px);
    }

    .quote-modal-action:disabled {
        opacity: 0.65;
        cursor: not-allowed;
    }

    .quote-modal-action--success {
        background: var(--color-state-success);
        border-color: var(--color-state-success);
    }

    .quote-modal-action--success:hover:not(:disabled),
    .quote-modal-action--success:focus {
        background: var(--color-state-success-strong);
        border-color: var(--color-state-success-strong);
        color: var(--color-text-inverse);
    }

    .quote-modal-action--brand {
        background: var(--color-brand-primary);
        border-color: var(--color-brand-primary);
    }

    .quote-modal-action--brand:hover:not(:disabled),
    .quote-modal-action--brand:focus {
        background: var(--color-brand-primary-strong);
        border-color: var(--color-brand-primary-strong);
        color: var(--color-text-inverse);
    }

    .submit-section {
        margin: 1.75rem 0px 1rem 0px;
        flex-wrap: wrap;
        p {
            margin: 0 0 0.5rem 0;
            width: 100%;
        }
    }
    .bpa-acknowledge {
        margin-bottom: 0.5rem !important;
        display: flex;
        align-items: center;
        width: 100%;
    }
    .bpa-acknowledge .form-check-input {
        margin-top: 0;
        margin-right: 0.5rem;
    }
</style>
